(function () {
  'use strict';

  if (customElements.get('scroll-media-effects')) return;

  var MOBILE_QUERY = '(max-width: 989px)';
  var REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

  // One scroll listener and one frame for every instance: all geometry is read first, then all
  // styles are written, so instances never force each other's layout. Only elements near the
  // viewport are updated.
  var scheduler = {
    members: new Set(),
    nearViewport: new Set(),
    frame: 0,
    observer: null,
    listening: false,

    add: function (element) {
      if (!this.observer) {
        this.observer = new IntersectionObserver(this.handleIntersection.bind(this), { rootMargin: '25% 0px' });
      }
      this.members.add(element);
      this.observer.observe(element);
      this.listen();
    },

    remove: function (element) {
      this.members.delete(element);
      this.nearViewport.delete(element);
      if (this.observer) this.observer.unobserve(element);
      if (!this.members.size) this.unlisten();
    },

    handleIntersection: function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) scheduler.nearViewport.add(entry.target);
        else scheduler.nearViewport.delete(entry.target);
      });
      this.request();
    },

    request: function () {
      if (this.frame || document.documentElement.classList.contains('is-scroll-locked')) return;
      this.frame = window.requestAnimationFrame(this.run);
    },

    run: function () {
      scheduler.frame = 0;
      if (document.documentElement.classList.contains('is-scroll-locked')) return;
      var elements = Array.from(scheduler.nearViewport);
      var viewportHeight = window.innerHeight;
      var measurements = elements.map(function (element) {
        return element.measure(viewportHeight);
      });
      elements.forEach(function (element, index) {
        element.apply(measurements[index]);
      });
    },

    listen: function () {
      if (this.listening) return;
      this.listening = true;
      this.handleScroll = this.handleScroll || this.request.bind(this);
      window.addEventListener('scroll', this.handleScroll, { passive: true });
      window.addEventListener('resize', this.handleScroll, { passive: true });
    },

    unlisten: function () {
      if (!this.listening) return;
      this.listening = false;
      window.removeEventListener('scroll', this.handleScroll);
      window.removeEventListener('resize', this.handleScroll);
      if (this.frame) window.cancelAnimationFrame(this.frame);
      this.frame = 0;
    }
  };

  class ScrollMediaEffects extends HTMLElement {
    connectedCallback() {
      if (this.isConnectedController) return;
      this.isConnectedController = true;

      var computedZoomScale =
        parseFloat(window.getComputedStyle(this).getPropertyValue('--scroll-media-zoom-scale')) || 1.2;

      this.desktopSettings = {
        effect: this.dataset.scrollEffectDesktop || this.dataset.scrollEffect || 'none',
        parallaxDirection:
          (this.dataset.parallaxDirectionDesktop || this.dataset.parallaxDirection) === 'horizontal'
            ? 'horizontal'
            : 'vertical',
        parallaxSpeed: this.parseParallaxSpeed(
          this.dataset.parallaxSpeedDesktop || this.dataset.parallaxSpeed
        ),
        zoomScale: this.parseZoomScale(this.dataset.zoomScaleDesktop, computedZoomScale),
        hasCustomParallaxSpeed:
          this.dataset.parallaxSpeedDesktop !== undefined || this.dataset.parallaxSpeed !== undefined
      };
      this.mobileSettings = {
        effect: this.dataset.scrollEffectMobile || 'inherit',
        parallaxDirection:
          this.dataset.parallaxDirectionMobile === 'horizontal' ? 'horizontal' : 'vertical',
        parallaxSpeed: this.parseParallaxSpeed(this.dataset.parallaxSpeedMobile),
        zoomScale: this.parseZoomScale(this.dataset.zoomScaleMobile, computedZoomScale),
        hasCustomParallaxSpeed: this.dataset.parallaxSpeedMobile !== undefined
      };
      // Parallax normally over-scales the visual so a clipped image never shows its frame edge;
      // a visual that moves as a whole (an unclipped card) sets data-parallax-scale="1".
      this.parallaxScale = parseFloat(this.dataset.parallaxScale) || 0;
      this.mediaVisuals = Array.from(this.querySelectorAll('[data-scroll-media-visual]'));
      this.videos = Array.from(this.querySelectorAll('theme-video video'));
      this.mobileMedia = window.matchMedia(MOBILE_QUERY);
      this.reducedMotionMedia = window.matchMedia(REDUCED_MOTION_QUERY);

      this.handleViewportChange = this.handleViewportChange.bind(this);
      this.handleVideoPlay = this.handleVideoPlay.bind(this);
      this.handleKeyboardNavigationChange = this.handleKeyboardNavigationChange.bind(this);

      this.mobileMedia.addEventListener('change', this.handleViewportChange);
      this.reducedMotionMedia.addEventListener('change', this.handleViewportChange);
      document.addEventListener('theme:keyboard-navigation-change', this.handleKeyboardNavigationChange);
      this.videos.forEach((video) => video.addEventListener('play', this.handleVideoPlay));
      this.handleViewportChange();
    }

    disconnectedCallback() {
      this.removeScrollListeners();
      this.mobileMedia?.removeEventListener('change', this.handleViewportChange);
      this.reducedMotionMedia?.removeEventListener('change', this.handleViewportChange);
      document.removeEventListener('theme:keyboard-navigation-change', this.handleKeyboardNavigationChange);
      this.videos?.forEach((video) => video.removeEventListener('play', this.handleVideoPlay));
      this.isConnectedController = false;
    }

    handleVideoPlay(event) {
      if (this.reducedMotionMedia.matches) event.currentTarget.pause();
    }

    handleKeyboardNavigationChange() {
      this.handleViewportChange();
    }

    handleViewportChange() {
      this.applyViewportSettings();

      var motionEnabled =
        !this.reducedMotionMedia.matches &&
        (this.effect === 'parallax' || this.effect === 'zoom_in' || this.effect === 'zoom_out') &&
        this.mediaVisuals.length > 0;

      this.removeScrollListeners();

      if (!motionEnabled) {
        this.writeTransform('0%', '0%', '1');
        if (this.reducedMotionMedia.matches) {
          this.videos.forEach((video) => {
            if (!video.paused) video.pause();
          });
        }
        return;
      }

      scheduler.add(this);
      scheduler.request();
    }

    applyViewportSettings() {
      var useMobileSettings = this.mobileMedia.matches && this.mobileSettings.effect !== 'inherit';
      var activeSettings = useMobileSettings ? this.mobileSettings : this.desktopSettings;

      var stickySuspended =
        document.body.classList.contains('theme-keyboard-navigation') && activeSettings.effect === 'sticky';
      this.effect = stickySuspended ? 'none' : activeSettings.effect;
      // CSS that resolves sticky from the server-rendered attributes reads this flag.
      this.toggleAttribute('data-scroll-effect-suspended', stickySuspended);
      this.parallaxDirection = activeSettings.parallaxDirection;
      this.parallaxSpeed = activeSettings.parallaxSpeed;
      this.zoomScale = activeSettings.zoomScale;
      this.hasCustomParallaxSpeed = activeSettings.hasCustomParallaxSpeed;

      // Keep the existing CSS contract as the active value while preserving
      // the desktop/mobile source settings in their dedicated data attributes.
      this.dataset.scrollEffect = this.effect;
    }

    parseParallaxSpeed(value) {
      return Math.min(Math.max(parseFloat(value) || 12, 1), 30);
    }

    parseZoomScale(value, fallback) {
      return Math.max(parseFloat(value) || fallback || 1.2, 1);
    }

    // Read phase (scheduler): geometry only, no style writes.
    measure(viewportHeight) {
      var rect = this.getBoundingClientRect();
      return {
        top: rect.top,
        height: rect.height,
        offsetHeight: this.dataset.scrollProgress === 'runway' ? this.offsetHeight : 0,
        viewportHeight: viewportHeight
      };
    }

    // Write phase (scheduler): styles only, no layout reads.
    apply(measurement) {
      var viewportHeight = measurement.viewportHeight;
      var travel = Math.max(viewportHeight + measurement.height, 1);
      var progress = Math.min(Math.max((viewportHeight - measurement.top) / travel, 0), 1);
      if (this.dataset.scrollProgress === 'runway') {
        var sectionRunway = measurement.offsetHeight - viewportHeight;
        travel = sectionRunway > 1 ? sectionRunway : viewportHeight;
        progress = Math.min(Math.max(-measurement.top / travel, 0), 1);
      }
      var translateX = 0;
      var translateY = 0;
      var scale = 1;
      var zoomDelta = this.zoomScale - 1;

      if (this.effect === 'parallax') {
        var parallaxOffset = this.parallaxSpeed / 2 - progress * this.parallaxSpeed;
        if (this.parallaxDirection === 'horizontal') {
          translateX = parallaxOffset;
        } else {
          translateY = parallaxOffset;
        }
        scale = this.parallaxScale || (this.hasCustomParallaxSpeed ? 1 + this.parallaxSpeed / 100 : 1.08);
      } else if (this.effect === 'zoom_in') {
        scale = 1 + progress * zoomDelta;
      } else if (this.effect === 'zoom_out') {
        scale = this.zoomScale - progress * zoomDelta;
      }

      this.writeTransform(translateX.toFixed(3) + '%', translateY.toFixed(3) + '%', scale.toFixed(4));
    }

    // The values go on the moving visuals themselves, not on this element: custom properties
    // inherit, so writing them here restyled the whole section (text, buttons…) on every frame.
    // Unchanged values are not written again.
    writeTransform(x, y, scale) {
      var key = x + ' ' + y + ' ' + scale;
      if (key === this.lastTransform) return;
      this.lastTransform = key;
      this.mediaVisuals.forEach(function (visual) {
        visual.style.setProperty('--scroll-media-translate-x', x);
        visual.style.setProperty('--scroll-media-translate-y', y);
        visual.style.setProperty('--scroll-media-scale', scale);
      });
    }

    removeScrollListeners() {
      scheduler.remove(this);
    }
  }

  customElements.define('scroll-media-effects', ScrollMediaEffects);
})();
